import React, { useEffect, useState } from "react";
import { Switch, Spinner } from "@chakra-ui/react";
import { FiChevronDown, FiChevronRight, FiRefreshCw } from "react-icons/fi";
import { useAvailableSections } from "../../hooks/useAvailableSections";
import type { SectionMeta } from "../../api/shortlist-report-api";
import type { SectionVisibility, FieldVisibility } from "../../hooks/useShortlistReportState";

interface SectionVisibilityPanelProps {
	sections: SectionVisibility;
	fields: FieldVisibility;
	onToggleSection: (sectionKey: string) => void;
	onToggleField: (sectionKey: string, fieldKey: string) => void;
	onInitSections: (sections: SectionVisibility) => void;
	onInitFields: (fields: FieldVisibility) => void;
	visibleSectionCount: number;
	onReset?: () => void;
}

const SectionVisibilityPanel: React.FC<SectionVisibilityPanelProps> = ({
	sections,
	fields,
	onToggleSection,
	onToggleField,
	onInitSections,
	onInitFields,
	visibleSectionCount,
	onReset,
}) => {
	const { data: availableSections, isLoading, isError } = useAvailableSections();
	const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({});

	// Initialize section/field visibility when metadata loads
	useEffect(() => {
		if (!availableSections || Object.keys(sections).length > 0) {
			return;
		}

		const initialSections: SectionVisibility = {};
		const initialFields: FieldVisibility = {};

		for (const section of availableSections) {
			initialSections[section.key] = true;
			if (section.fields) {
				initialFields[section.key] = {};
				for (const field of section.fields) {
					initialFields[section.key][field.field] = true;
				}
			}
		}

		onInitSections(initialSections);
		onInitFields(initialFields);
	}, [availableSections, sections, onInitSections, onInitFields]);

	const toggleExpanded = (sectionKey: string) => {
		setExpandedSections((prev) => ({
			...prev,
			[sectionKey]: !prev[sectionKey],
		}));
	};

	const isSectionVisible = (key: string) => sections[key] !== false;

	const isFieldVisible = (sectionKey: string, fieldKey: string) =>
		fields[sectionKey]?.[fieldKey] !== false;

	const isLastVisibleSection = (key: string) =>
		isSectionVisible(key) && visibleSectionCount <= 1;

	const getVisibleFieldCount = (section: SectionMeta) => {
		if (!section.fields) {
			return 0;
		}
		return section.fields.filter((f) => isFieldVisible(section.key, f.field)).length;
	};

	if (isLoading) {
		return (
			<div className="rounded-lg border border-gray-200 p-4">
				<h3 className="mb-3 text-sm font-semibold uppercase tracking-wide text-gray-700">
					Sections & Fields
				</h3>
				<div className="flex items-center justify-center py-4">
					<Spinner size="sm" className="mr-2" />
					<span className="text-sm text-gray-400">Loading sections...</span>
				</div>
			</div>
		);
	}

	if (isError || !availableSections) {
		return (
			<div className="rounded-lg border border-red-200 bg-red-50 p-4">
				<h3 className="mb-2 text-sm font-semibold uppercase tracking-wide text-red-700">
					Sections & Fields
				</h3>
				<p className="text-sm text-red-600">
					Failed to load available sections. Please try again.
				</p>
			</div>
		);
	}

	return (
		<div className="rounded-lg border border-gray-200 p-4">
			<div className="mb-1 flex items-center justify-between">
				<h3 className="text-sm font-semibold uppercase tracking-wide text-gray-700">
					Sections & Fields
				</h3>
				{onReset && (
					<button
						type="button"
						onClick={onReset}
						className="flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium text-blue-600 hover:bg-blue-50"
						title="Reset all sections and fields to visible"
					>
						<FiRefreshCw size={12} />
						Reset
					</button>
				)}
			</div>
			<p className="mb-3 text-xs text-gray-500">
				Toggle sections and fields to include in the report. At least one section must remain visible.
			</p>

			<div className="flex flex-col gap-1">
				{availableSections.map((section) => {
					const visible = isSectionVisible(section.key);
					const expanded = expandedSections[section.key] ?? false;
					const hasFields = section.fields && section.fields.length > 0;
					const lastVisible = isLastVisibleSection(section.key);

					return (
						<div key={section.key} className="rounded-md border border-gray-100">
							{/* Section header row */}
							<div className="flex items-center gap-2 px-3 py-2">
								{/* Expand/collapse toggle */}
								{hasFields && visible ? (
									<button
										type="button"
										onClick={() => toggleExpanded(section.key)}
										className="shrink-0 rounded p-0.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
										aria-label={expanded ? "Collapse fields" : "Expand fields"}
									>
										{expanded ? (
											<FiChevronDown size={14} />
										) : (
											<FiChevronRight size={14} />
										)}
									</button>
								) : (
									<div className="w-[18px] shrink-0" />
								)}

								{/* Section title + field count */}
								<div
									className="flex flex-1 cursor-pointer items-center gap-2"
									onClick={() => hasFields && visible ? toggleExpanded(section.key) : undefined}
								>
									<span
										className={`text-sm font-medium ${
											visible ? "text-gray-800" : "text-gray-400 line-through"
										}`}
									>
										{section.title}
									</span>
									{hasFields && visible && (
										<span className="text-xs text-gray-400">
											({getVisibleFieldCount(section)}/{section.fields!.length})
										</span>
									)}
								</div>

								{/* Section toggle */}
								<Switch
									isChecked={visible}
									onChange={() => onToggleSection(section.key)}
									isDisabled={lastVisible}
									colorScheme="blue"
									size="sm"
									aria-label={`Toggle ${section.title}`}
								/>
							</div>

							{/* Field-level toggles */}
							{hasFields && visible && expanded && (
								<div className="border-t border-gray-50 bg-gray-50/50 px-3 pb-2 pt-1">
									{section.fields!.map((field) => {
										const fieldVisible = isFieldVisible(section.key, field.field);
										return (
											<div
												key={field.field}
												className="flex items-center justify-between py-1 pl-6"
											>
												<span
													className={`text-xs ${
														fieldVisible ? "text-gray-600" : "text-gray-400 line-through"
													}`}
												>
													{field.label}
												</span>
												<Switch
													isChecked={fieldVisible}
													onChange={() => onToggleField(section.key, field.field)}
													colorScheme="blue"
													size="sm"
													aria-label={`Toggle ${field.label}`}
												/>
											</div>
										);
									})}
								</div>
							)}
						</div>
					);
				})}
			</div>
		</div>
	);
};

export default SectionVisibilityPanel;
